import moment from 'moment-timezone';

export const stationsEnum = {
  current: 'current',
  selection: 'selection',
  all: 'all',
};

export interface ChannelData {
  [channelId: string]: number;
}

export interface TimePoint {
  time: string;
  channelId: ChannelData[];
}

export interface StatusYoutubeMetadataByCountry {
  channelName: string;
  liveUrl: string;
  channelId: string;
  countryCode: string;
  category: string;
}

export const transformDataForUplot = (
  rawData: TimePoint[],
  statusYoutubeMetadataByCountry: StatusYoutubeMetadataByCountry[],
  timezone: string,
  pastelColors: string[],
) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return { data: [new Float64Array([]), new Float64Array([])], series: [] };
  }

  if (!statusYoutubeMetadataByCountry || !Array.isArray(statusYoutubeMetadataByCountry)) {
    return { data: [new Float64Array([]), new Float64Array([])], series: [] };
  }

  const timeValues: number[] = [];
  const channelNames: string[] = [];
  const channelDataMap: { [channelName: string]: (number | null)[] } = {};

  rawData.forEach((timePoint) => {
    // uplot expects seconds, not milliseconds
    const timestamp = moment(timePoint.time).tz(timezone).valueOf() / 1000;
    timeValues.push(timestamp);

    if (timePoint.channelId && Array.isArray(timePoint.channelId)) {
      timePoint.channelId.forEach((channelData: ChannelData) => {
        Object.keys(channelData).forEach((channelId) => {
          if (channelId === 'streams') return; // Skip streams property

          const channelMetadata = statusYoutubeMetadataByCountry.find(
            (metadata) => metadata.channelId === channelId,
          );
          const channelName = channelMetadata?.channelName || channelId;

          if (!channelNames.includes(channelName)) {
            channelNames.push(channelName);
          }
        });
      });
    }
  });

  // initialize all channel arrays with null values
  channelNames.forEach((channelName) => {
    channelDataMap[channelName] = new Array(timeValues.length).fill(null);
  });

  // fill in actual data where available
  rawData.forEach((timePoint, timeIndex) => {
    if (timePoint.channelId && Array.isArray(timePoint.channelId)) {
      timePoint.channelId.forEach((channelData: ChannelData) => {
        Object.entries(channelData).forEach(([channelId, viewers]) => {
          if (channelId === 'streams') return; // Skip streams property

          const channelMetadata = statusYoutubeMetadataByCountry.find(
            (metadata) => metadata.channelId === channelId,
          );
          const channelName = channelMetadata?.channelName || channelId;

          if (typeof viewers === 'number' && !isNaN(viewers) && isFinite(viewers)) {
            channelDataMap[channelName][timeIndex] = viewers;
          }
        });
      });
    }
  });

  const data: (number | null)[][] = [timeValues];
  channelNames.forEach((channelName) => {
    data.push(channelDataMap[channelName]);
  });

  const series = channelNames.map((channelName, index) => ({
    show: true,
    spanGaps: false,
    label: channelName,
    stroke: pastelColors[index % pastelColors.length],
    width: 2,
    points: {
      show: false,
    },
  }));

  // Check if time values are consistent
  if (timeValues.length > 1) {
    const intervals = [];
    for (let i = 1; i < Math.min(timeValues.length, 10); i++) {
      intervals.push(timeValues[i] - timeValues[i - 1]); // in seconds
    }
  }

  const typedData = data.map((arr) => {
    return arr.map((value) => {
      return value === null ? null : (value as number);
    });
  });

  return { data: typedData as any, series };
};
